import { useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { FilePdf, FileXls, Sparkle } from "@phosphor-icons/react";
import { localDateStr, localDateOffset } from "@/lib/date";

export default function Reports() {
  const today = localDateStr();
  const weekAgo = localDateOffset(-7);
  const [range, setRange] = useState({ start: weekAgo, end: today });
  const [busy, setBusy] = useState(false);
  const [dailyDate, setDailyDate] = useState(today);
  const [daily, setDaily] = useState("");
  const [weekly, setWeekly] = useState("");

  const download = async (kind) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/export/${kind}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(range),
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `al-axe-${range.start}_${range.end}.${kind === "pdf" ? "pdf" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  const genDaily = async () => {
    setBusy(true); setDaily("");
    try { const { data } = await api.post("/ai/daily-summary", { log_date: dailyDate }); setDaily(data.summary); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI error"); }
    finally { setBusy(false); }
  };
  const genWeekly = async () => {
    setBusy(true); setWeekly("");
    try { const { data } = await api.post("/ai/weekly-report", range); setWeekly(data.report); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI error"); }
    finally { setBusy(false); }
  };

  const inp = "rounded-xl border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400";

  return (
    <div data-testid="reports-page" className="space-y-6">
      <header>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Reports</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Exports & AI insights</h1>
      </header>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200/70 rounded-2xl p-6">
          <h2 className="text-xl font-semibold mb-4">Export range</h2>
          <div className="flex flex-wrap gap-2 items-center">
            <input data-testid="export-start" type="date" value={range.start} onChange={e=>setRange({...range, start: e.target.value})} className={inp}/>
            <span className="text-slate-400">→</span>
            <input data-testid="export-end" type="date" value={range.end} onChange={e=>setRange({...range, end: e.target.value})} className={inp}/>
          </div>
          <div className="flex gap-3 mt-4">
            <button data-testid="export-pdf" disabled={busy} onClick={()=>download("pdf")} className="inline-flex items-center gap-2 al-gradient text-white px-5 py-2.5 rounded-full font-medium"><FilePdf size={16}/> PDF</button>
            <button data-testid="export-excel" disabled={busy} onClick={()=>download("excel")} className="inline-flex items-center gap-2 border border-slate-200 px-5 py-2.5 rounded-full font-medium hover:bg-slate-50"><FileXls size={16}/> Excel</button>
          </div>
        </div>

        <div className="bg-white border border-slate-200/70 rounded-2xl p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Sparkle className="al-gradient-text" size={20}/> AI Daily Summary</h2>
          <div className="flex gap-2 mb-3">
            <input data-testid="ai-daily-date" type="date" value={dailyDate} onChange={e=>setDailyDate(e.target.value)} className={inp}/>
            <button data-testid="ai-daily-gen" disabled={busy} onClick={genDaily} className="al-gradient text-white px-5 py-2 rounded-full text-sm font-medium">{busy ? "Thinking…" : "Generate"}</button>
          </div>
          {daily && <pre data-testid="ai-daily-out" className="whitespace-pre-wrap text-sm text-slate-700 font-sans max-h-72 overflow-y-auto scrollbar-thin">{daily}</pre>}
        </div>
      </div>

      <div className="bg-white border border-slate-200/70 rounded-2xl p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Sparkle className="al-gradient-text" size={20}/> AI Weekly Report</h2>
        <p className="text-sm text-slate-500 mb-3">Uses the export range above.</p>
        <button data-testid="ai-weekly-gen" disabled={busy} onClick={genWeekly} className="al-gradient text-white px-5 py-2 rounded-full text-sm font-medium">{busy ? "Thinking…" : "Generate weekly report"}</button>
        {weekly && <pre data-testid="ai-weekly-out" className="mt-4 whitespace-pre-wrap text-sm text-slate-700 font-sans max-h-96 overflow-y-auto scrollbar-thin">{weekly}</pre>}
      </div>
    </div>
  );
}
